import { useMemo } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { BrandRouter } from "./brandRouter";
import { BrandSnapshotRegenerator } from "./brandSnapshotRegenerator";
import { DemoBrandStore } from "./DemoBrandStore";

/**
 * Hook to extract groupId and brandId from the current route
 * Pattern: /brands/wizard/{brandId}?groupId={groupId}
 * - brandId is optional in path (if exists = update, if not = create)
 * - groupId is optional query parameter (if exists = fixed, if not = selectable)
 */
export function useBrandContext() {
  const params = useParams<{
    brandId?: string;
  }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  return useMemo(() => {
    // let brandId = params.brandId;
    const brandId = params.brandId;
    const groupId = searchParams.get("groupId") || undefined;

    return {
      groupId,
      brandId,
      hasFixedGroupId: !!groupId, // If groupId is in query params, it's fixed
    };
  }, [params.brandId, searchParams, location.pathname]);
}

/**
 * Hook to get snapshot from location state
 */
export function useBrandSnapshot() {
  const location = useLocation();
  return useMemo(() => {
    return BrandSnapshotRegenerator.restoreSnapshot(location.state);
  }, [location.state]);
}

/**
 * Hook to create DemoBrandStore with snapshot
 * Store should only be recreated when brandId or groupId changes, not on step changes
 */
export function useDemoBrandStore(
  groupId: string | undefined,
  brandId: string | undefined,
  hasFixedGroupId: boolean
) {
  // Only recreate store when brandId or groupId changes, not when stepPaths change
  return useMemo(() => {
    return new DemoBrandStore(groupId, brandId, hasFixedGroupId);
  }, []);
}

/**
 * Hook to create BrandRouter with store
 * Router should only be recreated when store or stepPaths structure changes,
 * not on every pathname change (which happens on step navigation)
 */
export function useBrandRouter(
  brandStore: DemoBrandStore,
  stepPaths: string[]
) {
  const navigate = useNavigate();

  // Create a stable reference to stepPaths by stringifying it
  // This prevents recreation when stepPaths array reference changes but content is same
  const stepPathsKey = stepPaths.join("|");

  return useMemo(() => {
    return new BrandRouter(navigate, brandStore, stepPaths);
    // Only recreate when store or stepPaths content changes, not on every pathname change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, brandStore, stepPathsKey]);
}
